import React, { useState } from 'react';
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2, Send, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CONTACT_INFO, PHOTOBOOTHS, DELIVERY_INFO } from '../mock';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    photobooth: '',
    eventLocation: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert form data to URL-encoded format for Zapier
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('name', formData.name);
      urlEncodedData.append('email', formData.email);
      urlEncodedData.append('phone', formData.phone);
      urlEncodedData.append('eventDate', formData.eventDate);
      urlEncodedData.append('eventType', formData.eventType);
      urlEncodedData.append('photobooth', formData.photobooth);
      urlEncodedData.append('eventLocation', formData.eventLocation);
      urlEncodedData.append('message', formData.message);

      // Send to Zapier webhook
      const zapierWebhookUrl = process.env.REACT_APP_ZAPIER_WEBHOOK_URL;
      await fetch(zapierWebhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: urlEncodedData,
      });

      // With no-cors mode, we can't read the response, but the request is sent
      toast.success('Demande envoyée avec succès!', {
        description: 'Nous vous contacterons dans les 24 heures.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        eventType: '',
        photobooth: '',
        eventLocation: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer ou nous contacter directement par téléphone.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero with Background */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div 
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/10168433/pexels-photo-10168433.jpeg?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-captiva-cream/98 via-white/95 to-captiva-cream-dark/98 -z-10" />
        <div className="max-w-4xl mx-auto text-center relative">
          <MessageCircle className="h-16 w-16 mx-auto mb-6 text-captiva-gold" />
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Réservez votre photobooth ou demandez un devis personnalisé<br/>
            <span className="text-captiva-gold font-semibold">Réponse garantie sous 24h</span>
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-captiva-cream rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-captiva-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Téléphone</h3>
                    <a
                      href={`tel:${CONTACT_INFO.phone}`}
                      className="text-captiva-gold hover:text-captiva-gold-dark transition-colors"
                    >
                      {CONTACT_INFO.phone}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Lun-Dim: 9h-20h</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-captiva-cream rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-captiva-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                    <a
                      href={`mailto:${CONTACT_INFO.email}`}
                      className="text-captiva-gold hover:text-captiva-gold-dark transition-colors break-all"
                    >
                      {CONTACT_INFO.email}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Réponse sous 24h</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-captiva-cream rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-6 w-6 text-captiva-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                    <a
                      href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-captiva-gold hover:text-captiva-gold-dark transition-colors"
                    >
                      {CONTACT_INFO.whatsapp}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Chat direct</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-captiva-cream rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="h-6 w-6 text-captiva-gold" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-3">Zone d'intervention & livraison</h3>
                    <p className="text-sm text-gray-700 mb-2 font-medium">Toute la Belgique</p>
                    
                    <div className="bg-captiva-cream p-3 rounded-lg mb-3">
                      <p className="text-xs text-captiva-beige-dark font-medium">
                        📍 Gratuit dans un rayon de 30 km<br/>
                        <span className="text-xs text-gray-600">autour de 7170 Manage</span>
                      </p>
                    </div>
                    
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-700 font-medium">Forfait déplacement :</p>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>31-60 km</span>
                          <span className="text-captiva-gold font-semibold">+25€</span>
                        </div>
                        <div className="flex justify-between">
                          <span>61-100 km</span>
                          <span className="text-captiva-gold font-semibold">+50€</span>
                        </div>
                        <div className="flex justify-between">
                          <span>101-150 km</span>
                          <span className="text-captiva-gold font-semibold">+80€</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Au-delà de 150 km</span>
                          <span className="text-captiva-gold font-semibold">Sur devis</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3 italic">
                      Frais indiqués avant validation
                    </p>
                  </div>
                </div>
              </Card>

              {/* Trust Indicators */}
              <Card className="p-6 bg-gradient-to-br from-captiva-cream to-captiva-cream-dark border-2 border-captiva-gold-light">
                <h3 className="font-semibold text-gray-900 mb-4">Pourquoi nous choisir ?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Réponse rapide garantie</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Devis gratuit et transparent</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Installation professionnelle</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Support technique garanti</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Formulaire de réservation / Demande de devis
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jean Dupont"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jean.dupont@email.com"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium">
                        Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0491 12 34 56"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="eventDate" className="text-gray-700 font-medium">
                        Date de l'événement *
                      </Label>
                      <Input
                        id="eventDate"
                        name="eventDate"
                        type="date"
                        required
                        value={formData.eventDate}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="eventType" className="text-gray-700 font-medium">
                        Type d'événement *
                      </Label>
                      <select
                        id="eventType"
                        name="eventType"
                        required
                        value={formData.eventType}
                        onChange={handleChange}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-captiva-gold focus:border-transparent"
                      >
                        <option value="">Sélectionnez un type</option>
                        <option value="mariage">Mariage</option>
                        <option value="anniversaire">Anniversaire</option>
                        <option value="corporate">Événement corporate</option>
                        <option value="soiree-privee">Soirée privée</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="photobooth" className="text-gray-700 font-medium">
                        Photobooth souhaité *
                      </Label>
                      <select
                        id="photobooth"
                        name="photobooth"
                        required
                        value={formData.photobooth}
                        onChange={handleChange}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-captiva-gold focus:border-transparent"
                      >
                        <option value="">Sélectionnez un modèle</option>
                        <option value="classique">Photobooth Classique - À partir de 150€</option>
                        <option value="miroir">Miroir Booth - À partir de 250€</option>
                        <option value="indecis">Je ne sais pas encore</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="eventLocation" className="text-gray-700 font-medium">
                      Lieu de l'événement
                    </Label>
                    <Input
                      id="eventLocation"
                      name="eventLocation"
                      type="text"
                      value={formData.eventLocation}
                      onChange={handleChange}
                      placeholder="Ex: Bruxelles, Liège, Namur..."
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium">
                      Message / Informations complémentaires
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Parlez-nous de votre événement, vos besoins spécifiques, options souhaitées..."
                      rows={5}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-captiva-gold hover:bg-captiva-gold-dark text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        Envoyer ma demande
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    En soumettant ce formulaire, vous acceptez d'être contacté par Captiva Photobooth
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
